import { Column, ProcessCellForExportParams } from 'ag-grid-community'
import { isEmpty, isNil } from 'lodash-es'
import { AxeTradeCondition, Collection, CopyPasteFormat } from 'api'

import { AxeTradeConditionHelper } from 'model'
import { formatEvergreenNotice, getNotionalFormatOptions, makeFormatDirection } from 'view/shared/datagrid/modelFormatters'
import { CollectionColumnId } from 'view/routes/ColumnNames'
import { getFormattedTime, getTimeLeft, isOpenFin } from 'utils'
import { Locale, Translation } from 'locale'
import { StagingGridColumnId } from 'view/routes/staging/StagingOrdersGrid/StagingGridColumnNames'
import { LiveGridColumnId } from 'view/routes/requests/LiveNegotiationsGrid/LiveGridColumnNames'
import { CompletedGridColumnId } from 'view/routes/requests/completed/CompletedNegotiationsGrid/CompletedGridColumnNames'
import { AuditGridColumnId } from 'view/routes/audit/AuditGrid/AuditGridColumnNames'
import { DealsGridColumnId } from 'view/routes/deals/components/DealsGridColumnNames'
import { OpenReposGridColumnId } from 'view/routes/openRepos/components/OpenReposColumnNames'
import { MidTradeEventsGridColumnId } from 'view/routes/midTradeEvents/DataGrid/MidTradeEventsColumnNames'
import { IndexGridColumnId } from 'view/routes/admin/index/IndexScreen/IndexGridColumnNames'
import { formatValidFor } from 'view/routes/admin/index/IndexScreen/indexFormatters'
import { isNotEmpty } from 'utils/predicate'

declare global {
  interface Window {
    ClipboardItem: new (...args: any[]) => any
  }
}

export interface SendToClipboardParams {
  data: string
}

export interface IClipboardProcessor {
  sendToClipboard(params: SendToClipboardParams): Promise<void>

  setCopyPasteFormat(copyPasteFormat: CopyPasteFormat): void

  processRows(rows: string[]): string

  processCell(params: ProcessCellForExportParams): any
}

type ColumnIdAccessor = (col: Column) => string

interface ClipboardProcessorOptions {
  columnIdAccessor?: ColumnIdAccessor
  copyPasteFormat?: CopyPasteFormat
}

export interface ClipboardOutputBuilderConfig {
  cellsJoin: string
  cellWrap: (cellValue: string) => string
  rowWrap: (row: string) => string
  rowsJoin: string
  bodyWrap: (body: string) => string
}

const DEFAULT_CLIPBOARD_OUTPUT_BUILDER_CONFIG = {
  cellsJoin: '',
  cellWrap: (cellValue: string) => cellValue,
  rowWrap: (row: string) => row,
  rowsJoin: '',
  bodyWrap: (body: string) => body
}

const DEFAULT_OPTIONS = {
  columnIdAccessor: (column: Column) => column.getColId(),
  copyPasteFormat: CopyPasteFormat.TABULAR
}

abstract class ClipboardProcessorBase implements IClipboardProcessor {
  protected columnIdAccessor: ColumnIdAccessor
  protected copyPasteFormat: CopyPasteFormat

  constructor(options: ClipboardProcessorOptions = DEFAULT_OPTIONS) {
    this.columnIdAccessor = options.columnIdAccessor ?? DEFAULT_OPTIONS.columnIdAccessor
    this.copyPasteFormat = options.copyPasteFormat ?? DEFAULT_OPTIONS.copyPasteFormat
  }

  abstract processRows(rows: string[]): string

  abstract processCell(params: ProcessCellForExportParams): any

  protected transformDataToRows(data: string): string[] {
    return data.split('\r\n')
  }

  protected getHeaderRow(rows: string[]): string {
    return rows[1]
  }

  public setCopyPasteFormat(copyPasteFormat: CopyPasteFormat) {
    this.copyPasteFormat = copyPasteFormat
  }

  async sendToClipboard(params: SendToClipboardParams): Promise<void> {
    const rows = this.transformDataToRows(params.data)
    const output = this.processRows(rows)

    if (isOpenFin()) {
      if (output.includes('<table>')) {
        await window.fin.desktop.System.Clipboard.writeHtml(output, 'text/html')
      } else {
        await window.fin?.desktop.System.Clipboard.writeText(output, 'text')
      }
    } else {
      if (output.includes('<table>')) {
        const blob = new Blob([output], { type: 'text/html' })
        await window.navigator.clipboard.write([new window.ClipboardItem({ [blob.type]: blob })])
      } else {
        await window.navigator.clipboard.writeText(output)
      }
    }
  }
}

export class DefaultClipboardProcessor extends ClipboardProcessorBase {
  constructor(options?: ClipboardProcessorOptions) {
    super(options)

    this.processCell = this.processCell.bind(this)
    this.sendToClipboard = this.sendToClipboard.bind(this)
  }

  HEADERS_TO_REMOVE = [Locale.translate(Translation.TC_EMPTY_HEADER), Locale.translate(Translation.TC_GROUPING)]

  processCell(params: ProcessCellForExportParams) {
    const colDef = params.column.getColDef()

    if (colDef && colDef.cellRendererParams?.hideUnassignedLabel && colDef.cellRendererParams?.unassignedLabel === params.value) {
      return null
    }

    const colId = this.columnIdAccessor(params.column)

    switch (colId) {
      case CollectionColumnId.COLLECTION_NAME:
        const collection: Collection | null | undefined = params.value
        return collection?.name

      case LiveGridColumnId.RFQ_TIMER:
      case LiveGridColumnId.OTW_DURATION:
        return isNil(params.value) ? '' : getFormattedTime(getTimeLeft(params.value))

      case CompletedGridColumnId.RFQ_TIME:
        return getFormattedTime(params.value)

      case StagingGridColumnId.AXE_ROLL_UNWIND:
      case LiveGridColumnId.NEGOTIATION_AXE_ROLL_UNWIND:
      case CompletedGridColumnId.NEGOTIATION_AXE_ROLL_UNWIND:
      case DealsGridColumnId.DEAL_AXE_ROLL_UNWIND:
        const axeTradeCondition: AxeTradeCondition | null | undefined = params.value
        return axeTradeCondition ? AxeTradeConditionHelper.getName(axeTradeCondition) : ''

      case DealsGridColumnId.BLOCK_TRADE:
        return params.value === undefined ? '' : !!params.value

      case StagingGridColumnId.ORDER_DIRECTION:
      case LiveGridColumnId.NEGOTIATION_DIRECTION:
      case CompletedGridColumnId.NEGOTIATION_DIRECTION:
      case AuditGridColumnId.NEGOTIATION_DIRECTION:
      case DealsGridColumnId.DEAL_DIRECTION:
      case OpenReposGridColumnId.OPEN_REPOS_DIRECTION:
      case MidTradeEventsGridColumnId.MID_TRADE_EVENT_DIRECTION:
        return makeFormatDirection(params.context.userPreference.directionFormat)(params.value)

      case StagingGridColumnId.ORDER_NOTIONAL:
      case StagingGridColumnId.ORDER_TARGET_FILL_NOTIONAL:
      case LiveGridColumnId.NEGOTIATION_NOTIONAL:
      case LiveGridColumnId.NEGOTIATION_NOTIONAL_ORIGINAL:
      case LiveGridColumnId.NOTIONAL_DELTA:
      case LiveGridColumnId.NEGOTIATION_TOTAL_FILL_NOTIONAL:
      case LiveGridColumnId.NEGOTIATION_TARGET_FILL_NOTIONAL:
      case CompletedGridColumnId.NEGOTIATION_NOTIONAL:
      case AuditGridColumnId.NEGOTIATION_NOTIONAL:
      case DealsGridColumnId.DEAL_NOTIONAL:
      case OpenReposGridColumnId.OPEN_REPOS_NOTIONAL:
      case MidTradeEventsGridColumnId.MID_TRADE_EVENT_NOTIONAL:
      case MidTradeEventsGridColumnId.MID_TRADE_EVENT_NEW_NOTIONAL:
      case MidTradeEventsGridColumnId.MID_TRADE_EVENT_NEW_NOTIONAL_ORIGINAL:
      case MidTradeEventsGridColumnId.MID_TRADE_EVENT_NEW_NOTIONAL_DELTA:
        return isNil(params.value)
          ? ''
          : Locale.formatNumber(params.value, getNotionalFormatOptions(params.context.userPreference.amountFormat))

      case StagingGridColumnId.ORDER_START_DATE:
      case StagingGridColumnId.ORDER_END_DATE:
      case StagingGridColumnId.ORDER_CREATED_AT_DATE:
      case LiveGridColumnId.NEGOTIATION_START_DATE:
      case LiveGridColumnId.NEGOTIATION_START_DATE_TARGET:
      case LiveGridColumnId.NEGOTIATION_END_DATE:
      case LiveGridColumnId.NEGOTIATION_END_DATE_TARGET:
      case LiveGridColumnId.NEGOTIATION_CREATED_AT_DATE:
      case LiveGridColumnId.NEGOTIATION_UPDATED_AT_DATE:
      case CompletedGridColumnId.NEGOTIATION_START_DATE:
      case CompletedGridColumnId.NEGOTIATION_END_DATE:
      case CompletedGridColumnId.NEGOTIATION_CREATED_AT_DATE:
      case CompletedGridColumnId.NEGOTIATION_UPDATED_AT_DATE:
      case CompletedGridColumnId.NEGOTIATION_TRADE_DATE:
      case AuditGridColumnId.NEGOTIATION_START_DATE:
      case AuditGridColumnId.NEGOTIATION_END_DATE:
      case AuditGridColumnId.NEGOTIATION_CREATED_AT_DATE:
      case AuditGridColumnId.NEGOTIATION_UPDATED_AT_DATE:
      case AuditGridColumnId.NEGOTIATION_TRADE_DATE:
      case DealsGridColumnId.DEAL_START_DATE:
      case DealsGridColumnId.DEAL_END_DATE:
      case DealsGridColumnId.DEAL_RFQ_CREATED_AT_DATE:
      case DealsGridColumnId.DEAL_TRADE_DATE:
      case DealsGridColumnId.DEAL_UPDATED_AT_DATE:
      case OpenReposGridColumnId.OPEN_REPOS_START_DATE:
      case OpenReposGridColumnId.OPEN_REPOS_END_DATE:
      case OpenReposGridColumnId.OPEN_REPOS_TRADE_DATE:
      case OpenReposGridColumnId.OPEN_REPOS_UPDATED_AT_DATE:
      case MidTradeEventsGridColumnId.MID_TRADE_EVENT_EFFECTIVE_DATE:
      case MidTradeEventsGridColumnId.MID_TRADE_EVENT_START_DATE:
      case MidTradeEventsGridColumnId.MID_TRADE_EVENT_NEW_START_DATE:
      case MidTradeEventsGridColumnId.MID_TRADE_EVENT_END_DATE:
      case MidTradeEventsGridColumnId.MID_TRADE_EVENT_NEW_END_DATE:
      case MidTradeEventsGridColumnId.MID_TRADE_EVENT_TRADE_DATE:
      case MidTradeEventsGridColumnId.MID_TRADE_EVENT_LAST_UPDATED_DATE:
        return Locale.formatDate(params.value, params.context.userPreference.dateFormat)

      case StagingGridColumnId.ORDER_CREATED_AT_TIME:
      case LiveGridColumnId.NEGOTIATION_CREATED_AT_TIME:
      case LiveGridColumnId.NEGOTIATION_UPDATED_AT_TIME:
      case CompletedGridColumnId.NEGOTIATION_CREATED_AT_TIME:
      case CompletedGridColumnId.NEGOTIATION_UPDATED_AT_TIME:
      case CompletedGridColumnId.NEGOTIATION_TRADE_TIME:
      case DealsGridColumnId.DEAL_RFQ_CREATED_AT_TIME:
      case DealsGridColumnId.DEAL_TRADE_TIME:
      case DealsGridColumnId.DEAL_UPDATED_AT_TIME:
      case OpenReposGridColumnId.OPEN_REPOS_UPDATED_AT_TIME:
      case MidTradeEventsGridColumnId.MID_TRADE_EVENT_LAST_UPDATED_TIME:
        return Locale.formatTime(params.value)

      case IndexGridColumnId.INDEX_VALID_FOR:
        return isNil(params.value) ? '' : formatValidFor(params.value)

      case StagingGridColumnId.EVERGREEN_NOTICE_PERIOD:
      case LiveGridColumnId.EVERGREEN_NOTICE_PERIOD:
      case DealsGridColumnId.EVERGREEN_NOTICE_PERIOD:
        return formatEvergreenNotice(params.value)

      default:
        if (colDef.useValueFormatterForExport !== false && colDef.valueFormatter) {
          return params.formatValue(params.value)
        } else {
          return params.value
        }
    }
  }

  getFilteredRowToCellsTransformer(headerRow: string): (row: string) => string[] {
    return (row: string) => {
      const cells = row.split('\t')
      const idxsToOmit = headerRow
        .split('\t')
        .reduce<number[]>(
          (colIdxsToOmit, cell, idx) => (this.HEADERS_TO_REMOVE.includes(cell) ? [...colIdxsToOmit, idx] : colIdxsToOmit),
          []
        )
      return cells.filter((_, idx) => !idxsToOmit.includes(idx))
    }
  }

  buildOutput(
    rows: string[],
    filteredRowToCellsTransfromer: (row: string) => string[],
    config?: Partial<ClipboardOutputBuilderConfig>
  ): string {
    const { cellsJoin, cellWrap, rowWrap, rowsJoin, bodyWrap } = { ...DEFAULT_CLIPBOARD_OUTPUT_BUILDER_CONFIG, ...config }

    return bodyWrap(
      rows
        .map(row =>
          filteredRowToCellsTransfromer(row)
            .map(cellWrap)
            .join(cellsJoin)
        )
        .filter(isNotEmpty)
        .map(rowWrap)
        .join(rowsJoin)
    )
  }

  processRows(rows: string[]): string {
    const nonEmptyRows = rows.filter(line => !isEmpty(line) && !line.split('\t').every(cell => isEmpty(cell.trim())))
    const hasMultipleCells = nonEmptyRows[0] ? nonEmptyRows[0]?.includes('\t') : false

    if (hasMultipleCells) {
      const headerRow = this.getHeaderRow(rows)
      const filteredRowToCellsTransformer = this.getFilteredRowToCellsTransformer(headerRow)

      if (this.copyPasteFormat === CopyPasteFormat.TABULAR) {
        return this.buildOutput(nonEmptyRows, filteredRowToCellsTransformer, {
          cellWrap: (cellValue: string) => `<td>${cellValue}</td>`,
          rowWrap: (row: string) => `<tr>${row}</tr>`,
          bodyWrap: (body: string) => `<table>${body}</table>`
        })
      } else {
        return this.buildOutput(nonEmptyRows, filteredRowToCellsTransformer, {
          cellsJoin: `\t`,
          rowsJoin: '\r\n'
        })
      }
    } else {
      // remove headers and return single cell
      return this.buildOutput(nonEmptyRows.slice(1), (row: string) => [row], {
        cellsJoin: `\t`,
        rowsJoin: '\r\n'
      })
    }
  }
}
